import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, FLOOR_HEIGHT, TILE_COLORS } from '../constants';

interface FloorTile3DProps {
  position: [number, number, number];
  isCheckerDark?: boolean;
  entranceDelay?: number;
  isPath?: boolean;
}

export default function FloorTile3D({ position, isCheckerDark = false, entranceDelay = 0 }: FloorTile3DProps) {
  const groupRef = useRef<THREE.Group>(null);
  const scaleRef = useRef(0);
  const delayRef = useRef(entranceDelay);
  const entered = useRef(false);

  const topColor = isCheckerDark ? TILE_COLORS.floor.dark : TILE_COLORS.floor.light;
  const sideColor = TILE_COLORS.floor.side;

  // Tile dimensions: raised slab with visible sides for 3D depth
  const tileW = TILE_SIZE * 0.96;
  const tileH = FLOOR_HEIGHT;
  const sideH = FLOOR_HEIGHT * 0.6; // visible side depth

  useFrame((_, delta) => {
    if (!groupRef.current) return;

    if (!entered.current) {
      if (delayRef.current > 0) {
        delayRef.current -= delta;
        return;
      }
      scaleRef.current = Math.min(scaleRef.current + delta * 4, 1);
      if (scaleRef.current >= 1) {
        scaleRef.current = 1;
        entered.current = true;
      }
      groupRef.current.scale.set(scaleRef.current, scaleRef.current, scaleRef.current);
    }
  });

  return (
    <group ref={groupRef} position={position} scale={0}>
      {/* Top surface */}
      <RoundedBox
        args={[tileW, tileH * 0.4, tileW]}
        radius={0.02}
        smoothness={2}
        position={[0, tileH * 0.2, 0]}
        receiveShadow
      >
        <meshStandardMaterial
          color={topColor}
          metalness={0.03}
          roughness={0.7}
        />
      </RoundedBox>

      {/* Side depth (front face - visible from camera angle) */}
      <mesh position={[0, -tileH * 0.1, tileW / 2]}>
        <boxGeometry args={[tileW, sideH, 0.02]} />
        <meshStandardMaterial color={sideColor} roughness={0.85} metalness={0.02} />
      </mesh>

      {/* Side depth (right face) */}
      <mesh position={[tileW / 2, -tileH * 0.1, 0]}>
        <boxGeometry args={[0.02, sideH, tileW]} />
        <meshStandardMaterial color={sideColor} roughness={0.85} metalness={0.02} />
      </mesh>

      {/* Subtle border line on top */}
      <mesh position={[0, tileH * 0.41, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[tileW, tileW]} />
        <meshBasicMaterial color="black" transparent opacity={0.04} />
      </mesh>
    </group>
  );
}
